import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Global keyboard shortcuts hook
 * @param {Object} options
 * @param {Function} options.onToggleSidebar - Callback to toggle sidebar
 * @param {Object} options.tableActions - Table-specific actions { onNavigate, onSelect, onCheck, etc }
 */
export function useKeyboardShortcuts({
  onToggleSidebar,
  tableActions,
  enabled = true,
} = {}) {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);

  const handleKeyDown = useCallback(
    (event) => {
      if (!enabled) return;

      // Ignore if user is typing in an input field
      const target = event.target;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true'
      ) {
        return;
      }

      const key = event.key.toLowerCase();
      const ctrl = event.ctrlKey || event.metaKey;
      const shift = event.shiftKey;

      // Global shortcuts
      switch (key) {
        case '?':
          if (shift) {
            event.preventDefault();
            setShowHelp((prev) => !prev);
          }
          break;

        case '[':
          if (onToggleSidebar) {
            event.preventDefault();
            onToggleSidebar();
          }
          break;

        case ']':
          if (onToggleSidebar) {
            event.preventDefault();
            onToggleSidebar();
          }
          break;

        case 'g':
          // Wait for second key (vim-style go-to)
          // This is handled by a separate effect
          break;

        case 'd':
          if (event.code === 'KeyD' && !ctrl && !shift) {
            // Check if previous key was 'g'
            const lastKey = window.__lastKey;
            if (lastKey === 'g') {
              event.preventDefault();
              navigate('/');
            }
          }
          break;

        case 'p':
          if (!ctrl && !shift) {
            const lastKey = window.__lastKey;
            if (lastKey === 'g') {
              event.preventDefault();
              navigate('/projects');
            }
          }
          break;

        case 'a':
          if (!ctrl && !shift) {
            const lastKey = window.__lastKey;
            if (lastKey === 'g') {
              event.preventDefault();
              navigate('/alerts');
            }
          }
          break;

        case 's':
          if (!ctrl && !shift) {
            const lastKey = window.__lastKey;
            if (lastKey === 'g') {
              event.preventDefault();
              navigate('/settings');
            }
          }
          break;

        case 'i':
          if (!ctrl && !shift) {
            const lastKey = window.__lastKey;
            if (lastKey === 'g') {
              event.preventDefault();
              navigate('/import');
            }
          }
          break;

        case 'escape':
          setShowHelp(false);
          break;

        default:
          break;
      }

      // Table navigation shortcuts
      if (tableActions) {
        switch (key) {
          case 'j':
            if (!ctrl && !shift) {
              event.preventDefault();
              tableActions.onNavigateDown?.();
            }
            break;

          case 'k':
            if (!ctrl && !shift) {
              event.preventDefault();
              tableActions.onNavigateUp?.();
            }
            break;

          case 'enter':
            if (!ctrl && !shift) {
              event.preventDefault();
              tableActions.onOpen?.();
            }
            break;

          case 'c':
            if (!ctrl && !shift) {
              event.preventDefault();
              tableActions.onCheck?.();
            }
            break;

          case 'm':
            if (!ctrl && !shift) {
              event.preventDefault();
              tableActions.onRefreshMetrics?.();
            }
            break;

          case 'x':
            if (!ctrl && !shift) {
              event.preventDefault();
              tableActions.onToggleSelect?.();
            }
            break;

          case ' ':
            if (!ctrl && !shift) {
              event.preventDefault();
              tableActions.onToggleSelect?.();
            }
            break;

          default:
            break;
        }
      }

      // Store last key for combo shortcuts
      window.__lastKey = key;
      setTimeout(() => {
        window.__lastKey = null;
      }, 500);
    },
    [enabled, navigate, onToggleSidebar, tableActions]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return {
    showHelp,
    setShowHelp,
  };
}

/**
 * Table navigation hook for J/K navigation
 */
export function useTableNavigation(items, options = {}) {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const navigate = useNavigate();

  const onNavigateDown = useCallback(() => {
    setFocusedIndex((prev) => Math.min(prev + 1, items.length - 1));
  }, [items.length]);

  const onNavigateUp = useCallback(() => {
    setFocusedIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const onOpen = useCallback(() => {
    const item = items[focusedIndex];
    if (item && options.getItemPath) {
      navigate(options.getItemPath(item));
    }
  }, [focusedIndex, items, navigate, options]);

  const onToggleSelect = useCallback(() => {
    const item = items[focusedIndex];
    if (item) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(item.id)) {
          next.delete(item.id);
        } else {
          next.add(item.id);
        }
        return next;
      });
    }
  }, [focusedIndex, items]);

  const onSelectAll = useCallback(() => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((item) => item.id)));
    }
  }, [items, selectedIds.size]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  return {
    focusedIndex,
    setFocusedIndex,
    selectedIds,
    setSelectedIds,
    tableActions: {
      onNavigateDown,
      onNavigateUp,
      onOpen,
      onToggleSelect,
      onCheck: options.onCheck,
      onRefreshMetrics: options.onRefreshMetrics,
    },
    onSelectAll,
    clearSelection,
  };
}

// Keyboard shortcuts help content
export const KEYBOARD_SHORTCUTS = [
  {
    category: 'Navigation',
    shortcuts: [
      { keys: ['g', 'd'], description: 'Go to Dashboard' },
      { keys: ['g', 'p'], description: 'Go to Projects' },
      { keys: ['g', 'a'], description: 'Go to Alerts' },
      { keys: ['g', 's'], description: 'Go to Settings' },
      { keys: ['g', 'i'], description: 'Go to Import' },
      { keys: ['[', ']'], description: 'Toggle sidebar' },
    ],
  },
  {
    category: 'Table Navigation',
    shortcuts: [
      { keys: ['j'], description: 'Move down' },
      { keys: ['k'], description: 'Move up' },
      { keys: ['Enter'], description: 'Open selected item' },
      { keys: ['Space', 'x'], description: 'Toggle selection' },
    ],
  },
  {
    category: 'Actions',
    shortcuts: [
      { keys: ['c'], description: 'Check selected backlink(s)' },
      { keys: ['m'], description: 'Refresh Moz metrics' },
      { keys: ['?'], description: 'Show this help' },
      { keys: ['Esc'], description: 'Close modal/dialog' },
    ],
  },
];
